import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ConversionFunnel from '../../components/analytics/ConversionFunnel';
import PerformanceMetrics from '../../components/analytics/PerformanceMetrics';
import ROICalculator from '../../components/analytics/ROICalculator';
import TrendChart from '../../components/analytics/TrendChart';
import apiClient from '../../lib/api';

interface AnalyticsDashboardProps {
  projectId?: number;
}

export default function AnalyticsDashboard({ projectId = 0 }: AnalyticsDashboardProps) {
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('7d');

  // Fetch performance overview
  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['analytics', 'performance', timeframe, projectId],
    queryFn: async () => {
      const params = new URLSearchParams({ timeframe });
      if (projectId > 0) params.append('project_id', projectId.toString());
      return apiClient.get(`/analytics/performance?${params.toString()}`);
    },
  });

  // Fetch conversion funnel
  const { data: funnelData, isLoading: funnelLoading } = useQuery({
    queryKey: ['analytics', 'funnel', projectId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (projectId > 0) params.append('project_id', projectId.toString());
      return apiClient.get(`/analytics/conversion-funnel?${params.toString()}`);
    },
  });

  // Fetch ROI metrics
  const { data: roiData, isLoading: roiLoading } = useQuery({
    queryKey: ['analytics', 'roi', timeframe],
    queryFn: async () => {
      const params = new URLSearchParams({ timeframe });
      return apiClient.get(`/analytics/roi?${params.toString()}`);
    },
  });

  // Fetch trends
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['analytics', 'trends', timeframe, projectId],
    queryFn: async () => {
      const params = new URLSearchParams({ timeframe });
      if (projectId > 0) params.append('project_id', projectId.toString());
      return apiClient.get(`/analytics/trends?${params.toString()}`);
    },
  });

  // Fetch daily activity data
  const { data: dailyActivityData } = useQuery({
    queryKey: ['daily-activity', timeframe],
    queryFn: async () => {
      const days = timeframe === '24h' ? 1 : timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
      const response = await apiClient.request<{ success: boolean; data: Array<{ date: string; opportunities: number; comments: number }> }>(
        `/wp-json/redd-miner-leads/v1/analytics/daily-activity?days=${days}`
      );
      return response.data || [];
    },
  });

  const isLoading = performanceLoading || funnelLoading || roiLoading || trendsLoading;

  return (
    <div className="analytics-dashboard min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Analytics Dashboard
        </h1>
        <p className="text-gray-600">
          Track performance, conversions, and ROI across your Reddit campaigns
        </p>
      </div>

      {/* Timeframe Selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Timeframe</h2>
          <div className="flex gap-2">
            {(['24h', '7d', '30d', '90d'] as const).map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  timeframe === tf
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tf === '24h' ? '24 Hours' : tf === '7d' ? '7 Days' : tf === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Performance Metrics Grid */}
          <PerformanceMetrics
            data={performanceData?.data?.metrics}
            trends={performanceData?.data?.trends}
            timeframe={timeframe}
          />

          {/* Daily Activity Chart */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Daily Activity</h2>
              <p className="text-sm text-gray-600 mt-1">
                Opportunities discovered and comments posted per day
              </p>
            </div>
            <div className="p-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyActivityData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                      tick={{ fontSize: 12 }}
                      stroke="#6b7280"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
                    <Tooltip
                      labelFormatter={(value) => new Date(value as string).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                      formatter={(value) => value === 'opportunities' ? 'Opportunities Discovered' : 'Comments Posted'}
                    />
                    <Line
                      type="monotone"
                      dataKey="opportunities"
                      name="opportunities"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="comments"
                      name="comments"
                      stroke="#10B981"
                      strokeWidth={3}
                      dot={{ fill: '#10B981', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Conversion Funnel</h2>
              <p className="text-sm text-gray-600 mt-1">
                Track how opportunities convert through each stage
              </p>
            </div>
            <div className="p-6">
              <ConversionFunnel data={funnelData?.data} />
            </div>
          </div>

          {/* ROI Calculator */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">ROI Analysis</h2>
              <p className="text-sm text-gray-600 mt-1">
                Revenue, costs, and return on investment by project
              </p>
            </div>
            <div className="p-6">
              <ROICalculator data={roiData?.data?.projects} />
            </div>
          </div>

          {/* Trend Chart */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Performance Trends</h2>
              <p className="text-sm text-gray-600 mt-1">
                Daily performance metrics over time
              </p>
            </div>
            <div className="p-6">
              <TrendChart
                data={trendsData?.data?.snapshots}
                timeframe={timeframe}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
